import React, { useState, useEffect } from 'react';
import { Search, User, CheckCircle, Printer, AlertCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const ClearanceCertificate = () => {
  const [eligibleStudents, setEligibleStudents] = useState([]);
  const [ineligibleStudents, setIneligibleStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showIneligible, setShowIneligible] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchEligibleStudents();
  }, []);

  const fetchEligibleStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/fees/clearance-eligible-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEligibleStudents(response.data.eligible_students);
      setIneligibleStudents(response.data.ineligible_students);
    } catch (error) {
      console.error('Error fetching students:', error);
      alert('Failed to fetch student data');
    } finally {
      setLoading(false);
    }
  };

  const displayStudents = showIneligible ? ineligibleStudents : eligibleStudents;
  
  const filteredStudents = displayStudents.filter(student =>
    student.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePrintCertificate = async (student) => {
    if (student.balance > 0) {
      alert(`Cannot generate certificate. Student has outstanding balance of ₹${student.balance.toLocaleString('en-IN')}`);
      return;
    }

    setGeneratingFor(student.id);
    try {
      const response = await axios.get(
        `${API_URL}/api/fees/clearance-certificate/${student.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clearance_certificate_${student.admission_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating certificate:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to generate certificate';
      alert(errorMessage);
    } finally {
      setGeneratingFor(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Fee Clearance Certificate</h1>
        <p className="text-sm text-gray-500 mt-1">Generate fee clearance certificates for eligible students</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 font-medium">Eligible Students (Zero Balance)</p>
              <p className="text-3xl font-bold text-green-800 mt-1">{eligibleStudents.length}</p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-700 font-medium">Ineligible Students (Have Dues)</p>
              <p className="text-3xl font-bold text-orange-800 mt-1">{ineligibleStudents.length}</p>
            </div>
            <AlertCircle className="h-12 w-12 text-orange-500" />
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start">
          <CheckCircle className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Eligibility Criteria:</strong> Fee clearance certificates are ONLY issued to students who have:
            </p>
            <ul className="list-disc list-inside text-sm text-blue-700 mt-2 ml-2">
              <li>Paid 100% of total course fee</li>
              <li>Paid all fines (if any)</li>
              <li>Paid all other fees completely</li>
              <li>Zero outstanding balance</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Toggle and Search */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setShowIneligible(false)}
              className={`px-4 py-2 rounded-lg font-medium text-sm ${
                !showIneligible 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Eligible ({eligibleStudents.length})
            </button>
            <button
              onClick={() => setShowIneligible(true)}
              className={`px-4 py-2 rounded-lg font-medium text-sm ${
                showIneligible 
                  ? 'bg-orange-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Ineligible ({ineligibleStudents.length})
            </button>
          </div>
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search students by name or admission number..."
              className="pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Details</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No.</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Paid</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">Loading students...</td></tr>
            ) : filteredStudents.length === 0 ? (
              <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">No students found</td></tr>
            ) : (
              filteredStudents.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{student.full_name}</div>
                        <div className="text-sm text-gray-500">{student.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{student.admission_number}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{student.course_name}</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">₹{student.total_paid.toLocaleString('en-IN')}</td>
                  <td className="px-6 py-4 text-sm text-right font-semibold">
                    <span className={student.balance <= 0 ? 'text-green-600' : 'text-red-600'}>
                      ₹{Math.abs(student.balance).toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {student.balance <= 0 ? (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Cleared
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        <XCircle className="h-3 w-3 mr-1" />
                        Due: ₹{student.balance.toLocaleString('en-IN')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handlePrintCertificate(student)}
                      disabled={student.balance > 0 || generatingFor === student.id}
                      className={`flex items-center gap-1 font-medium text-sm ml-auto ${
                        student.balance > 0 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : generatingFor === student.id
                            ? 'text-gray-400'
                            : 'text-blue-600 hover:text-blue-900'
                      }`}
                    >
                      <Printer className="h-4 w-4" />
                      {generatingFor === student.id ? 'Generating...' : 'Print Certificate'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClearanceCertificate;